import React, { useContext, useEffect, useRef, useState } from 'react';
import { Text, Flex, Icon, Menu, MenuButton, Portal, MenuList, MenuItem, IconButton, useDisclosure, Box, Button, Tooltip } from '@chakra-ui/react';
import { MdArrowForward, MdDevices, MdDragIndicator, MdFace, MdMoreVert, MdOutlineEdit, MdOutlineLayers, MdOutlineLayersClear, MdArrowOutward, MdSync, MdOutlineWarning } from 'react-icons/md';
import InsightTags from '@/insight/components/insightTags';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/modal';
import PersonaProvider from '@/persona/lib/personaProvider';
import PersonaThumbnail from '@/persona/components/personaThumbnail';
import ProductLogo from '@/products/components/productLogo';
import ProductProvider from '@/products/lib/productProvider';
import { Link, useNavigate } from 'react-router-dom';
import SharedExperienceTag from '@/components/sharedExperienceTag';
import { useSharedExperiencesApi } from '../../lib/useSharedExperiencesApi';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';
import { SharedExperiencePageContext } from '../lib/sharedExperiencePageContext';
import ExperienceGroupInSharedExperienceNameForm from './experienceGroupInSharedExperienceNameForm';
import MoveGroupInSharedExperienceToGroupForm from './moveGroupInSharedExperienceToGroupForm';
import useTranslation from '../../../lib/i18n/useTranslation';

export default function ExperienceGroupInSharedExperienceListItem(props) {
  const { t } = useTranslation();
  const {
    sharedExperienceId,
    rights,
  } = useContext(SharedExperiencesContext);
  const {
    refreshExperienceList,
    refreshPersonaList,
    refreshProductList,
    refreshSharedExperienceList,
    experienceGroupList,
  } = useContext(SharedExperiencePageContext);
  const {
    data,
    dragHoverEnable,
    dragHover = false
  } = props;
  const sharedExperiencesApi = useSharedExperiencesApi();
  const navigate = useNavigate();
  const [personaList, setPersonaList] = useState(false);
  const [productList, setProductList] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const modalEditGroupName = useDisclosure();
  const modalMoveToGroup = useDisclosure();
  const modalConvertToSharedExperience = useDisclosure();
  const [isConverting, setIsConverting] = useState(false);
  const ref1 = useRef();
  const ref2 = useRef();
  const ungroup = async () => {
    try {
      await sharedExperiencesApi.ungroupExperienceGroup(data.id)
      await Promise.all([
        refreshExperienceList(),
        refreshPersonaList(),
        refreshProductList()
      ])
    } catch (error) {
      console.error(error);
    }
  }
  const convertToSharedExperience = async () => {
    try {
      setIsConverting(true);
      await sharedExperiencesApi.convertGroupToSharedExperience(sharedExperienceId, data.id);
      modalConvertToSharedExperience.onClose();
      await Promise.all([
        refreshExperienceList(),
        refreshPersonaList(),
        refreshProductList(),
        refreshSharedExperienceList()
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsConverting(false);
    }
  }
  useEffect(() => {
    if (data) {
      if (data.personas && data.personas[0] !== null) {
        setPersonaList(data.personas)
      }
      if (data.products && data.products[0] !== null) {
        setProductList(data.products)
      }
    }
  }, [data]);
  if (!data || (!data.group?.name && !data.name)) {
    return null;
  }
  return (
    <>
      <Flex
        px={3}
        py={2}
        backgroundColor={(dragHoverEnable && dragHover) ? "background.green" : "white"}
        role={'group'}
        _hover={{
          background: "background.grey",
        }}
        position={"relative"}
        justifyContent={"space-between"}
        height={"90px"}
        cursor={"pointer"}

      >
        {rights.can_update && (
          <Flex
            position={"absolute"}
            top="0"
            left={"0"}
            height={"100%"}
            opacity={0}
            _groupHover={{ opacity: '1' }}
            cursor={"grab"}
            py={2}
            pr={3}
          >
            <Icon
              as={MdDragIndicator}
              boxSize={'20px'}
              color={"text.light.grey"}
              mr={2}
            />
          </Flex>
        )}
        <Flex
          flexDirection={"column"}
          flex={1}
        >
          <Flex
            flex={1}
            alignItems={"flex-start"}
          >
            <Flex
              alignItems={"flex-start"}
              mb={1}
              flex={1}
            >
              <Icon
                as={MdOutlineLayers}
                boxSize={'15px'}
                mr={2}
                mt={"3px"}
                color="text.medium.green"
              />
              <Flex flexDirection={"column"}>
                <Text
                  noOfLines={2}
                  color="text.medium.green"
                  fontWeight={"500"}
                >{data?.group?.name || data?.name || 'Unnamed Group'}</Text>
                <Flex
                alignItems="center"
                gap={2}
                mt={1}
                >
                  {(data.shared_experiences?.id || data.shared_experiences_id) && data.shared_experience_name && (
                    <SharedExperienceTag 
                      sharedExperienceName={data.shared_experience_name} 
                      size="sm"
                    />
                  )}
                  <Text
                    fontSize={"12px"}
                    color="text.medium.grey"
                  >{data.experience_count} Experience{(data.experience_count > 0 ? 's' : '')}</Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex
            flexDirection={"column"}
            alignSelf={"stretch"}
            height={"100%"}
            alignItems={"flex-end"}
            >
            <Flex flex={1}
            alignItems={"flex-start"}>
            {data?.insights_count && (
              <InsightTags
                nbPostiveFeedback={data.insights_count?.positive_count || 0}
                nbNegativeFeedback={data.insights_count?.negative_count || 0}
                nbImprovementFeedback={data.insights_count?.improvement_count || 0}
                nbBehavior={data.insights_count?.behavior_count || 0}
                nbMotivation={data.insights_count?.motivation_count || 0}
              />
            )}
            </Flex>
            <Flex>
              {(personaList && personaList.length > 0) && (
                <Flex
                  alignItems={"center"}
                  mr={3}
                >
                  <Icon
                    as={MdFace}
                    size="20"
                    color="text.medium.grey"
                  />
                  {personaList.map((p, i) => (
                    <Tooltip
                      label={p.name}
                      key={i}
                    >
                      <Link to={`/persona/${p.id}`} onClick={(e) => {
                        e.stopPropagation()
                      }}>
                        <Flex
                          ml={2}
                        >
                          <PersonaProvider
                            personaId={p.id}
                            originalPersona={p}
                            needRights={false}
                            needRole={false}
                            needEditionRequest={false}
                          >
                            <PersonaThumbnail
                              size={20}
                              hasAcronym={true}
                            />
                          </PersonaProvider>
                        </Flex>
                      </Link>
                    </Tooltip>
                  ))}
                </Flex>
              )}
              {(productList && productList.length > 0) && (
                <Flex alignItems={"center"}>
                  <Icon
                    as={MdDevices}
                    size="20"
                    color="text.medium.grey"
                  />
                  {productList && productList.map((p, i) => (
                    <Tooltip
                      label={p.name}
                      key={i}
                    >
                      <Link to={`/products/${p.id}`} onClick={(e) => {
                        e.stopPropagation()
                      }}>
                        <Flex
                          ml={2}
                        >
                          <ProductProvider
                            productId={p.id}
                            orignalProduct={p}
                            needRights={false}
                            needRole={false}
                            needEditionRequest={false}
                          >
                            <ProductLogo
                              size={20}
                              hasAcronym={true}
                            />
                          </ProductProvider>
                        </Flex>
                      </Link>
                    </Tooltip>
                  ))}
                </Flex>
              )}
            </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Flex>
          <Flex
            height={"100%"}
            pl={3}
            alignItems={"center"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {(!rights.can_update) &&
              <IconButton
                icon={<MdArrowForward />}
                fontSize={"18px"}
                size="md"
                variant='greyTransparent'
                opacity={"0"}
                _groupHover={{ opacity: '1' }}
                onClick={(e) => {
                  navigate(`/shared-experiences/${sharedExperienceId}?group-id=${data.id}`, { replace: true });
                }}
              />
            }
            {rights.can_update &&
              <Menu >
                {({ isOpen }) => (
                  <>
                    <MenuButton
                      isActive={isOpen}
                      as={IconButton}
                      icon={<MdMoreVert />}
                      fontSize={"18px"}
                      size="md"
                      variant='greyTransparent'
                    />
                    <Portal>
                      <MenuList zIndex="popover">
                        {!data.source_shared_experiences_id && (
                          <MenuItem
                            onClick={modalEditGroupName.onOpen}
                          >
                            <Icon as={MdOutlineEdit} mr={2} color="text.medium.dark" />
                            <Text color="text.medium.dark">{t('common.rename')}</Text>
                          </MenuItem>
                        )}
                        <MenuItem onClick={(e) => {
                          navigate(`/shared-experiences/${sharedExperienceId}?group-id=${data.id}`, { replace: true });
                        }} >
                          <Icon as={MdArrowForward} mr={2} color="text.medium.dark" />
                          <Text color="text.medium.dark">{t('group.goToGroup')}</Text>
                        </MenuItem>
                        {(experienceGroupList && experienceGroupList.length > 0) && !data.source_shared_experiences_id && (
                          <MenuItem onClick={modalMoveToGroup.onOpen} >
                            <Icon as={MdArrowOutward} mr={2} color="text.medium.dark" />
                            <Text color="text.medium.dark">Move to Group</Text>
                          </MenuItem>
                        )}
                        {!data.source_shared_experiences_id && (
                          <MenuItem
                            onClick={ungroup}
                          >
                            <Icon as={MdOutlineLayersClear} mr={2} color="text.medium.dark" />
                            <Text color="text.medium.dark">Ungroup</Text>
                          </MenuItem>
                        )}
                        {!data.source_shared_experiences_id && (
                          <MenuItem
                            onClick={modalConvertToSharedExperience.onOpen}
                          >
                            <Icon as={MdSync} mr={2} color="text.medium.dark" />
                            <Text color="text.medium.dark">{t('group.convertToSharedExperience')}</Text>
                          </MenuItem>
                        )}
                      </MenuList>
                    </Portal>
                  </>
                )}
              </Menu>
            }
          </Flex>
        </Flex>
        <Modal
          isOpen={modalEditGroupName.isOpen}
          onClose={modalEditGroupName.onClose}
        >
          <ModalOverlay />
          <ModalContent >
            <ModalHeader>Edit Group Name</ModalHeader>
            <ModalBody >
              <Box p={3}>
                <ExperienceGroupInSharedExperienceNameForm
                  hideSave={true}
                  groupId={data.id}
                  group={{ name: data.group ? data.group.name : data.name }}
                  onSubmitting={() => {
                    setIsAdding(true);
                  }}
                  onSubmitted={() => {
                    Promise.all([
                      refreshExperienceList(),
                      refreshPersonaList(),
                      refreshProductList()
                    ])
                    setIsAdding(false);
                    modalEditGroupName.onClose()
                  }}
                  onValidityChange={(v) => {
                    setIsValid(v);
                  }}
                  ref={ref1}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='greenOutline'
                onClick={modalEditGroupName.onClose}
              >Close</Button>
              <Button
                variant="greenFill"
                ml={2}
                isLoading={isAdding}
                isDisabled={!isValid}
                onClick={() => {
                  ref1.current.click();
                }}>Save</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={modalMoveToGroup.isOpen}
          onClose={modalMoveToGroup.onClose}
        >
          <ModalOverlay />
          <ModalContent >
            <ModalHeader>Move Group to Another Group</ModalHeader>
            <ModalBody >
              <Box p={3}>
                <MoveGroupInSharedExperienceToGroupForm
                  hideSave={true}
                  groupId={data.id}
                  onSubmitting={() => {
                    setIsAdding(true);
                  }}
                  onSubmitted={() => {
                    Promise.all([
                      refreshExperienceList(),
                      refreshPersonaList(),
                      refreshProductList()
                    ])
                    setIsAdding(false);
                    modalMoveToGroup.onClose()
                  }}
                  onValidityChange={(v) => {
                    setIsValid(v);
                  }}
                  ref={ref2}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='greenOutline'
                onClick={modalMoveToGroup.onClose}
              >Close</Button>
              <Button
                variant="greenFill"
                ml={2}
                isLoading={isAdding}
                isDisabled={!isValid}
                onClick={() => {
                  ref2.current.click();
                }}>Save</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={modalConvertToSharedExperience.isOpen} onClose={modalConvertToSharedExperience.onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('group.convertToSharedExperience')}</ModalHeader>
            <ModalBody p={3}>
              <Flex
                border="1px"
                borderColor="divider.grey"
                borderRadius="2px"
                backgroundColor="white"
                alignItems={'center'}
                p={3}
              >
                <Icon
                  as={MdOutlineWarning}
                  color={'text.medium.yellow'}
                  boxSize={'18px'}
                  mr={3}
                />
                <Text textStyle="body" color={'text.dark.grey'}>
                  Are you sure you want to convert the group "{data?.group?.name || data?.name}" into a shared experience? This action cannot be undone.
                </Text>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='greenOutline'
                onClick={modalConvertToSharedExperience.onClose}
              >{t('common.cancel')}</Button>
              <Button
                variant="yellowFill"
                ml={2}
                isLoading={isConverting}
                onClick={convertToSharedExperience}
              >{t('group.convert')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex >
    </>
  );
} 